import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Play, Pause, SkipForward, SkipBack, X, Maximize2, Crown, ShieldAlert,
  Landmark, Boxes, Radar, Award, TrendingUp,
} from "lucide-react";

/**
 * Boardroom Presentation Mode — presentation-only kiosk that cycles through
 * cabinet-grade dashboards. Reuses existing endpoints. No backend impact.
 */

const SLIDES = [
  { key: "situation",   title: "National Situation Room",         subtitle: "Live cadastre snapshot & national alerts", icon: Landmark,     accent: "#C99A2E" },
  { key: "performance", title: "National Sovereign Performance",  subtitle: "13-dimension composite index",             icon: Award,        accent: "#C99A2E" },
  { key: "cabinet",     title: "Cabinet Command Centre",          subtitle: "Urgency-scored priorities",                icon: ShieldAlert,  accent: "#7B2FBE" },
  { key: "twin",        title: "National Digital Twin",           subtitle: "Live twin of the sovereign estate",        icon: Boxes,        accent: "#4FA3D1" },
  { key: "forecast",    title: "Strategic Forecast Centre",       subtitle: "5-year forward series across 11 metrics",  icon: TrendingUp,   accent: "#6BB88A" },
];

const useSlideData = () => ({
  situation:   useQuery({ queryKey: ["br-sit"],  queryFn: async () => (await api.get("/cadastre360/sovereign/situation-room")).data }).data,
  performance: useQuery({ queryKey: ["br-perf"], queryFn: async () => (await api.get("/cadastre360/gsros/performance-index")).data }).data,
  cabinet:     useQuery({ queryKey: ["br-cab"],  queryFn: async () => (await api.get("/cadastre360/gsros/cabinet-command-centre")).data }).data,
  twin:        useQuery({ queryKey: ["br-twin"], queryFn: async () => (await api.get("/cadastre360/sovereign/twin")).data }).data,
  forecast:    useQuery({ queryKey: ["br-fc"],   queryFn: async () => (await api.get("/cadastre360/gsros/strategic-forecast-centre")).data }).data,
});

const KpiTile = ({ label, value, accent = "#C99A2E" }) => (
  <div className="rounded-sm border p-4" style={{ borderColor: accent + "40", background: "rgba(7,27,43,0.55)" }}>
    <div className="font-mono uppercase tracking-[0.22em] text-[11px]" style={{ color: accent }}>{label}</div>
    <div className="font-display font-bold text-[#F6F8FA] mt-1 text-[42px] leading-tight break-words">{value}</div>
  </div>
);

const SlideBody = ({ slide, data }) => {
  if (slide === "situation") {
    const k = data?.cabinet_kpis || {};
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiTile label="Parcels registered" value={Number(k.parcels_total || 0).toLocaleString()} accent="#C99A2E" />
        <KpiTile label="Mining active"     value={Number(k.mining_active || 0).toLocaleString()} accent="#C99A2E" />
        <KpiTile label="Survey records"    value={Number(k.survey_records || 0).toLocaleString()} accent="#4FA3D1" />
        <KpiTile label="Applications open" value={Number(k.applications_open || 0).toLocaleString()} accent="#6BB88A" />
        <KpiTile label="Disputes open"     value={Number(k.disputes_open || 0).toLocaleString()} accent="#E67E7E" />
        <KpiTile label="Mining conflicts"  value={Number(k.mining_conflicts || 0).toLocaleString()} accent="#E67E7E" />
        <KpiTile label="Revenue collected" value={`USD ${Number(k.revenue_collected_usd || 0).toLocaleString()}`} accent="#4FA3D1" />
        <KpiTile label="Outstanding"       value={`USD ${Number(k.revenue_outstanding_usd || 0).toLocaleString()}`} accent="#C99A2E" />
      </div>
    );
  }
  if (slide === "performance") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-sm border p-6" style={{ borderColor: "#C99A2E40", background: "rgba(7,27,43,0.55)" }}>
          <div className="font-mono uppercase tracking-[0.22em] text-[11px] text-[#C99A2E]">National score</div>
          <div className="flex items-baseline gap-4 mt-2">
            <div className="font-display font-bold text-[#F6F8FA] text-[96px] leading-none">{data?.national_score ?? "—"}</div>
            <div className="rounded-sm px-3 py-1 font-mono text-[14px] uppercase" style={{ background: "#C99A2E30", color: "#C99A2E" }}>Grade {data?.grade}</div>
          </div>
          <div className="mt-4 text-[13px] italic text-[#DCE3E8] border-l-2 border-[#C99A2E] pl-3">{data?.ai_commentary}</div>
        </div>
        <div className="rounded-sm border p-6" style={{ borderColor: "#4FA3D140", background: "rgba(7,27,43,0.55)" }}>
          <div className="font-mono uppercase tracking-[0.22em] text-[11px] text-[#4FA3D1]">International comparison</div>
          <div className="mt-3 space-y-1.5">
            {Object.entries(data?.international_comparison || {}).map(([c, s]) => (
              <div key={c} className="flex items-center gap-3 text-[13px]">
                <div className="w-40 truncate text-[#F6F8FA]">{c}</div>
                <div className="flex-1 h-2 rounded-sm bg-white/5 relative">
                  <div className="absolute inset-y-0 left-0 rounded-sm"
                       style={{ width: `${s}%`, background: c === "Zimbabwe" ? "#C99A2E" : "#4FA3D1" }} />
                </div>
                <div className="w-12 text-right font-mono text-[12px] text-[#F6F8FA]">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (slide === "cabinet") {
    const items = (data?.top_priority_items || []).slice(0, 8);
    const c = (u) => u >= 90 ? "#E67E7E" : u >= 75 ? "#C99A2E" : "#4FA3D1";
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((it) => (
          <div key={it.id} className="rounded-sm border p-4" style={{ borderColor: c(it.urgency_score) + "50", background: "rgba(7,27,43,0.55)" }}>
            <div className="flex items-center gap-3">
              <div className="rounded-sm px-2.5 py-1 font-mono text-[16px]" style={{ background: c(it.urgency_score) + "30", color: c(it.urgency_score) }}>
                {it.urgency_score}
              </div>
              <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-[#7C93A5]">{it.portfolio}</div>
            </div>
            <div className="mt-2 text-[15px] text-[#F6F8FA] font-medium">{it.detail}</div>
            <div className="mt-1 text-[12px] text-[#DCE3E8]">→ {it.recommended_action}</div>
          </div>
        ))}
      </div>
    );
  }
  if (slide === "twin") {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {Object.entries(data?.coverage || {}).map(([k, v]) => (
          <KpiTile key={k} label={k.replace(/_/g, " ")} value={v} accent="#4FA3D1" />
        ))}
        <div className="rounded-sm border p-4 md:col-span-3" style={{ borderColor: "#4FA3D140", background: "rgba(7,27,43,0.55)" }}>
          <div className="font-mono uppercase tracking-[0.22em] text-[11px] text-[#4FA3D1]">Layers</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(data?.layers || []).map((l) => (
              <span key={l} className="rounded-sm px-2 py-0.5 text-[12px] font-mono text-[#F6F8FA]" style={{ background: "#4FA3D125" }}>{l}</span>
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (slide === "forecast") {
    const series = (data?.royalties_usd_m || []);
    const max = Math.max(...series.map((s) => s.value), 1);
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-sm border p-6" style={{ borderColor: "#6BB88A40", background: "rgba(7,27,43,0.55)" }}>
          <div className="font-mono uppercase tracking-[0.22em] text-[11px] text-[#6BB88A]">Royalties forecast (USD m)</div>
          <div className="mt-4 flex items-end gap-3 h-48">
            {series.map((s) => (
              <div key={s.year} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-t-sm" style={{ height: `${(s.value / max) * 100}%`, background: "#6BB88A" }} />
                <div className="font-mono text-[11px] text-[#F6F8FA]">{s.value}</div>
                <div className="font-mono text-[10px] text-[#7C93A5]">{s.year}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-sm border p-6" style={{ borderColor: "#C99A2E40", background: "rgba(7,27,43,0.55)" }}>
          <div className="font-mono uppercase tracking-[0.22em] text-[11px] text-[#C99A2E]">Government revenue (USD m)</div>
          <div className="mt-4 space-y-2">
            {(data?.government_revenue_usd_m || []).map((s) => (
              <div key={s.year} className="flex items-center gap-3">
                <div className="w-16 font-mono text-[13px] text-[#7C93A5]">{s.year}</div>
                <div className="flex-1 h-2 rounded-sm bg-white/5 relative">
                  <div className="absolute inset-y-0 left-0 rounded-sm bg-[#C99A2E]" style={{ width: `${Math.min(100, s.value / 30)}%` }} />
                </div>
                <div className="w-24 text-right font-mono text-[13px] text-[#F6F8FA]">{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export const Boardroom = () => {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [tick, setTick] = useState(0);
  const [notes, setNotes] = useState("");
  const data = useSlideData();

  const next = useCallback(() => setIdx((i) => (i + 1) % SLIDES.length), []);
  const prev = useCallback(() => setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => { next(); setTick(0); }, 12000);
    const timer = setInterval(() => setTick((v) => Math.min(v + 1, 12)), 1000);
    return () => { clearInterval(t); clearInterval(timer); };
  }, [playing, next]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === " ") { e.preventDefault(); setPlaying((v) => !v); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const enterFullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
  };

  const slide = SLIDES[idx];
  const SlideIcon = slide.icon;

  return (
    <div className="min-h-[calc(100vh-90px)] flex flex-col" data-testid="page-boardroom"
         style={{ background: "linear-gradient(160deg, #071B2B 0%, #0B3558 100%)" }}>
      <div className="flex items-center gap-3 px-6 py-3 border-b border-white/10">
        <Crown size={22} className="text-[#C99A2E]" />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px] text-[#C99A2E]">Cadastre360™ · Boardroom Presentation Mode</div>
          <div className="font-display font-bold text-[#F6F8FA] text-lg">Cabinet-ready command view</div>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={prev} data-testid="boardroom-prev" className="rounded-sm p-1.5 border border-white/10 text-[#F6F8FA]"><SkipBack size={14} /></button>
          <button onClick={() => setPlaying((v) => !v)} data-testid="boardroom-play"
                  className="rounded-sm px-3 py-1.5 border font-mono text-[10px] uppercase tracking-[0.14em] inline-flex items-center gap-1"
                  style={{ borderColor: playing ? "#C99A2E" : "rgba(255,255,255,0.1)", color: playing ? "#C99A2E" : "#F6F8FA" }}>
            {playing ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Play</>}
          </button>
          <button onClick={next} data-testid="boardroom-next" className="rounded-sm p-1.5 border border-white/10 text-[#F6F8FA]"><SkipForward size={14} /></button>
          <button onClick={enterFullscreen} data-testid="boardroom-fs" className="rounded-sm p-1.5 border border-white/10 text-[#F6F8FA]"><Maximize2 size={14} /></button>
          <Link to="/app/executive" data-testid="boardroom-exit"
                className="rounded-sm px-2.5 py-1.5 border border-white/10 text-[10px] font-mono uppercase text-[#F6F8FA] inline-flex items-center gap-1">
            <X size={12} /> Exit
          </Link>
        </div>
      </div>

      <div className="px-6 py-2 flex flex-wrap items-center gap-1 border-b border-white/5">
        {SLIDES.map((s, i) => (
          <button key={s.key} onClick={() => { setIdx(i); setTick(0); }}
                  data-testid={"boardroom-slide-" + s.key}
                  className="rounded-sm px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.14em] inline-flex items-center gap-1 border"
                  style={{
                    background: i === idx ? s.accent + "30" : "transparent",
                    color: i === idx ? "#F6F8FA" : "#B3C2CC",
                    borderColor: i === idx ? s.accent : "rgba(255,255,255,0.08)",
                  }}>
            <s.icon size={10} style={{ color: s.accent }} /> {s.title}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <div className="font-mono text-[10px] text-[#7C93A5] uppercase tracking-[0.16em]">Auto-advance</div>
          <div className="w-24 h-1 rounded-sm bg-white/5 relative">
            <div className="absolute inset-y-0 left-0 rounded-sm bg-[#C99A2E]" style={{ width: `${(tick / 12) * 100}%` }} />
          </div>
        </div>
      </div>

      <div className="flex-1 p-8">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-baseline gap-3 mb-6">
            <SlideIcon size={26} style={{ color: slide.accent }} />
            <div>
              <div className="font-display font-bold text-[#F6F8FA] text-[32px] leading-tight">{slide.title}</div>
              <div className="text-[13px] text-[#DCE3E8]">{slide.subtitle}</div>
            </div>
            <div className="ml-auto font-mono text-[11px] uppercase tracking-[0.16em] text-[#7C93A5]">
              Slide {idx + 1} of {SLIDES.length}
            </div>
          </div>
          <SlideBody slide={slide.key} data={data[slide.key]} />
        </div>
      </div>

      <div className="border-t border-white/10 px-6 py-3 flex items-center gap-3">
        <div className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#7C93A5]">Speaker notes</div>
        <input value={notes} onChange={(e) => setNotes(e.target.value)}
               data-testid="boardroom-notes"
               placeholder="Type talking points — visible only to you"
               className="flex-1 bg-transparent border border-white/10 rounded-sm px-3 py-1.5 text-[12px] text-[#F6F8FA]" />
        <div className="font-mono text-[10px] text-[#7C93A5]">← / → navigate · space to pause</div>
      </div>
    </div>
  );
};

export default Boardroom;
